import React, { useEffect, useMemo, useRef, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { FormGroup } from 'react-bootstrap';
import { MultiSelect } from 'react-multi-select-component';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty, set } from 'lodash';
import { useTranslation } from 'react-i18next';
import * as indicatorAction from './store/Indicator.action';
import * as commonAction from '../TodoCommon.action';
import { Year, getYears, months, createOptions, checkArray, Page_Size } from '../../../../Components/helper';
import { getSupplierBrands } from '../CostingTsx/store/Costing.action';
import { getProductStatusDetails } from '../../../RangePlan/ProductList/store/ProductList.action';
import * as systemMasterAction from '../../../SystemMasters/Store/SystemMasters.action';
import { CurrentYearItemRenderer } from '../../../../Components/Utility/CurrentYearDropdown';

function IndicatorFilter() {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { t: tReports } = useTranslation('todo-reports');

  const reducers = useSelector((redState) => redState);
  const {
    UserDetails: {
      userInfo: { userId },
      isSupplierLoggedIn,
      isCompanyUserLoggedIn,
    },

    SystemMastersDetails: { multiSubBrandList, pcTypeList, licensorList, licenseList, likenessList },
    IndicatorTodoListData: {
      indicatorFilterValues: {
        selected_Season,
        selected_Year = [],
        selected_Month = [],
        selected_Brand,
        selected_SubBrand,
        selected_Supplier,
        selected_Category1,
        selected_Category2,
        selected_Category3,
        selected_Category4,
        selectedProductType,
        selectedLicensor,
        selectedLicense,
        selectedLikeness,
        sortOrder,
        sortPropertyName,
        selected_CreatedBy,
        selected_ModifiedBy,
        selected_Status,
      },
    },
  } = reducers;

  const [state, setState] = useState({
    seasonOptions: [],
    yearOptions: createOptions(getYears(), 'name', '_id'),
    monthOptions: createOptions(months, 'name', '_id'),
    brandOptions: [],
    subBrandOptions: [],
    supplierOptions: [],
    category1Options: [],
    category2Options: [],
    category3Options: [],
    category4Options: [],
    usersOptions: [],
    statusOptions: [],
    productTypeOptions: [],
  });
  const {
    seasonOptions,
    yearOptions,
    monthOptions,
    brandOptions,
    subBrandOptions,
    supplierOptions,
    category1Options,
    category2Options,
    category3Options,
    category4Options,
    usersOptions,
    statusOptions,
    productTypeOptions,
  } = state;
  const [isApply, setIsApply] = useState(false);
  const isFetchingCatsRef = useRef(false);

  const [subBrandAccess, setSubBrandAccess] = useState(false);
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);
  const [licensorAccess, setLicensorAccess] = useState(false);

  const monthYearOptions = useMemo(() => {
    const years = Array.isArray(selected_Year) ? [...selected_Year].sort((a, b) => a - b) : [];
    if (years.length === 0) return [];
    const combined = [];
    years.forEach((year) => {
      monthOptions.forEach((month) => {
        combined.push({
          ...month,
          _id: `${month.value}_${year}`,
          label: `${month.label} ${year}`,
          value: `${month.value}_${year}`,
        });
      });
    });
    return combined;
  }, [selected_Year, monthOptions]);

  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(systemMasterAction.getCatBasedOnPT());
        setCatBasedOnPTData(!!res);
      } catch (e) {
        setCatBasedOnPTData(false);
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  useEffect(() => {
    const fetchSubBrandAccess = async () => {
      const subBrandAccessRes = await dispatch(systemMasterAction.getSubBrandAccess());
      setSubBrandAccess(subBrandAccessRes?.data || false);
    };
    fetchSubBrandAccess();
  }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    const fetchLicensorSetup = async () => {
      const res = await dispatch(systemMasterAction.getLicensorAccess()).catch(() => null);
      const hasAccess = !!res?.isActiveLicensor;
      setLicensorAccess(hasAccess);
      if (hasAccess) {
        dispatch(systemMasterAction.getLicensorDetails({ loggedInUserId: userId, isActive: true }));
      }
    };
    fetchLicensorSetup();
  }, [dispatch, userId]);

  useEffect(() => {
    if (!userId) return;
    const saved = localStorage.getItem('indicatorTodoListFilter');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (parsed?.selectedLicensor?.length > 0 || parsed?.selectedLicense?.length > 0 || parsed?.selectedLikeness?.length > 0) {
        dispatch(
          indicatorAction.filterSortData({
            selectedLicensor: parsed.selectedLicensor || [],
            selectedLicense: parsed.selectedLicense || [],
            selectedLikeness: parsed.selectedLikeness || [],
          }),
        );
      }
      if (parsed?.selectedLicensor?.length > 0) {
        dispatch(
          systemMasterAction.getLicenseDetails({
            licensorIds: parsed.selectedLicensor,
            loggedInUserId: userId,
            isActive: true,
          }),
        );
      }
      if (parsed?.selectedLicensor?.length > 0 && parsed?.selectedLicense?.length > 0) {
        dispatch(
          systemMasterAction.getLikenessDetails({
            licensorIds: parsed.selectedLicensor,
            licenseIds: parsed.selectedLicense,
            loggedInUserId: userId,
            isActive: true,
          }),
        );
      }
    } catch (e) {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId]);

  useEffect(() => {
    dispatch(
      systemMasterAction.getPCTypesDetails(
        {
          loggedInUserId: userId,
          isActive: true,
          tier: 'Tier1',
        },
        true,
      ),
    );
  }, [dispatch, userId]);

  useEffect(() => {
    const isTrue = JSON.parse(localStorage.getItem('indicatorTodoListFilter'));
    if (isTrue) {
      for (const keys in isTrue) {
        if (isTrue[keys].length > 0) {
          setIsApply(true);
        }
      }
    }
  }, []);

  // Rehydrate saved filter selections into Redux on initial mount so dependent fetches have parent IDs
  useEffect(() => {
    const saved = localStorage.getItem('indicatorTodoListFilter');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      const allEmpty = [
        selected_Season,
        selected_Year,
        selected_Month,
        selected_Brand,
        selected_SubBrand,
        selected_Supplier,
        selectedProductType,
        selected_Category1,
        selected_Category2,
        selected_Category3,
        selected_Category4,
        selected_CreatedBy,
        selected_ModifiedBy,
        selected_Status,
      ].every((v) => !Array.isArray(v) || v.length === 0);
      if (allEmpty) {
        dispatch(indicatorAction.filterSortData(parsed));
      }
    } catch (e) {
      console.log('error while api call', e);
    }
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(pcTypeList)) {
      setState((prev) => ({ ...prev, productTypeOptions: createOptions(pcTypeList, 'name', '_id') }));
    }
  }, [pcTypeList]);
  const handleSelectChange = async (value, name) => {
    const obj = {};
    set(obj, `${name}`, value);
    if (name === 'selectedProductType' && catBasedOnPTData) {
      obj.selected_Category1 = [];
      obj.selected_Category2 = [];
      obj.selected_Category3 = [];
      obj.selected_Category4 = [];
    }
    if (name === 'selected_Category1') {
      obj.selected_Category2 = [];
    }
    if (
      ['selected_Category1', 'selected_Category2'].some((v) => {
        return name.indexOf(v) >= 0;
      })
    ) {
      obj.selected_Category3 = [];
      obj.selected_Category4 = [];
    }
    if (
      ['selected_Category3'].some((v) => {
        return name.indexOf(v) >= 0;
      })
    ) {
      obj.selected_Category4 = [];
    }
    if (name === 'selected_Brand') {
      obj.selected_SubBrand = [];
      setState((prev) => ({ ...prev, subBrandOptions: [] }));
    }
    if (name === 'selected_Year') {
      const remainingYears = value || [];
      const cleanedMonths = (selected_Month || []).filter((monthStr) => {
        const match = monthStr.match(/^([a-zA-Z]+)_(\d{4})$/);
        if (!match) return true;
        return remainingYears.includes(Number(match[2]));
      });
      obj.selected_Month = cleanedMonths;
    }
    await dispatch(indicatorAction.filterSortData(obj));
  };

  const licensorOptions = useMemo(() => (checkArray(licensorList) ? createOptions(licensorList, 'name', '_id') : []), [licensorList]);
  const licenseOptions = useMemo(() => (checkArray(licenseList) ? createOptions(licenseList, 'name', '_id') : []), [licenseList]);
  const likenessOptions = useMemo(() => (checkArray(likenessList) ? createOptions(likenessList, 'name', '_id') : []), [likenessList]);

  const handleFilterLicense = (val, key) => {
    const newData = { [key]: val };
    if (key === 'selectedLicensor') {
      newData.selectedLicense = [];
      newData.selectedLikeness = [];
      dispatch(systemMasterAction.clearLikenessData());
      if (val.length > 0) {
        dispatch(systemMasterAction.getLicenseDetails({ licensorIds: val, loggedInUserId: userId, isActive: true }));
      } else {
        dispatch(systemMasterAction.clearLicenseData());
      }
    }
    if (key === 'selectedLicense') {
      newData.selectedLikeness = [];
      dispatch(systemMasterAction.clearLikenessData());
      if (val.length > 0 && checkArray(selectedLicensor)) {
        dispatch(systemMasterAction.getLikenessDetails({ licensorIds: selectedLicensor, licenseIds: val, loggedInUserId: userId, isActive: true }));
      }
    }
    dispatch(indicatorAction.filterSortData(newData));
  };

  const clearAll = async () => {
    const paramsObj = {
      seasons: [],
      year: [],
      month: [],
      brand: [],
      subBrand: [],
      supplier: [],
      colour: [],
      productTypeId: [],
      category1: [],
      category2: [],
      category3: [],
      category4: [],
      licensorId: [],
      licenseId: [],
      likenessId: [],
      status: {},
      pageIndex: 1,
      pageSize: Page_Size,
      type: 'progress-indicator',
    };
    const sortinObj = {
      selected_Season: [],
      selected_Year: [],
      selected_Month: [],
      selected_Brand: [],
      selected_SubBrand: [],
      selected_Supplier: [],
      selectedProductType: [],
      selected_Category1: [],
      selected_Category2: [],
      selected_Category3: [],
      selected_Category4: [],
      selectedLicensor: [],
      selectedLicense: [],
      selectedLikeness: [],
      selected_CreatedBy: [],
      selected_ModifiedBy: [],
      selected_Status: [],
      loading: true,
      sortOrder,
      sortPropertyName,
      pageIndex: 1,
      pageSize: Page_Size,
    };
    dispatch(systemMasterAction.clearLicenseData());
    dispatch(systemMasterAction.clearLikenessData());
    localStorage.removeItem('indicatorTodoListFilter');

    const res = await dispatch(indicatorAction.getIndicatorList(paramsObj));
    setIsApply(false);
    if (res) await dispatch(indicatorAction.filterSortData(sortinObj));
  };

  const applyFilter = async () => {
    setIsApply(true);
    const paramsObj = {
      seasons: selected_Season,
      year: selected_Year,
      month: selected_Month,
      brand: selected_Brand,
      subBrand: selected_SubBrand,
      supplier: selected_Supplier,
      colour: [],
      productTypeId: Array.isArray(selectedProductType) ? selectedProductType : [],
      category1: selected_Category1,
      category2: selected_Category2,
      category3: selected_Category3,
      category4: selected_Category4,
      createdBy: selected_CreatedBy,
      modifiedBy: selected_ModifiedBy,
      status: selected_Status,
      licensorId: selectedLicensor,
      licenseId: selectedLicense,
      likenessId: selectedLikeness,
      loading: true,
      sortOrder,
      sortPropertyName,
      pageIndex: 1,
      pageSize: Page_Size,
      type: 'progress-indicator',
    };

    const paramsObj1 = {
      selected_Season,
      selected_Year,
      selected_Month,
      selected_Brand,
      selected_SubBrand,
      selected_Supplier,
      selected_Category1,
      selected_Category2,
      selected_Category3,
      selected_Category4,
      selectedProductType,
      selected_CreatedBy,
      selected_ModifiedBy,
      selected_Status,
      selectedLicensor,
      selectedLicense,
      selectedLikeness,
    };

    localStorage.setItem('indicatorTodoListFilter', JSON.stringify(paramsObj1));
    await dispatch(indicatorAction.getIndicatorList(paramsObj));
    const obj = {
      pageIndex: 1,
      pageSize: Page_Size,
    };
    await dispatch(indicatorAction.filterSortData(obj));
  };

  const fetchCategories = async () => {
    for (const cat of [1, 2]) {
      const category = await dispatch(
        commonAction.fetchCategoryAction({
          accessedFrom: 'masters',
          categoryType: cat,
          loggedInUserId: userId,
        }),
      );
      setState((prevState) => ({ ...prevState, [`category${cat}Options`]: createOptions(category, 'name', '_id') }));
    }

    if (Array.isArray(selected_Category1) && selected_Category1.length && Array.isArray(selected_Category2) && selected_Category2.length) {
      const c1s = selected_Category1;
      const c2s = selected_Category2;
      const calls3 = [];
      c1s.forEach((c1) => {
        c2s.forEach((c2) => {
          calls3.push(
            dispatch(
              commonAction.fetchCategoryAction({
                accessedFrom: 'masters',
                categoryType: 3,
                loggedInUserId: userId,
                categoryOneId: c1,
                categoryTwoId: c2,
              }),
            ),
          );
        });
      });
      const results3 = await Promise.all(calls3);
      const merged3 = [];
      const seen3 = new Set();
      results3.forEach((res) => {
        const arr = Array.isArray(res?.data) ? res.data : res;
        if (Array.isArray(arr)) {
          arr.forEach((item) => {
            const key = String(item?._id ?? item?.value ?? '');
            if (key && !seen3.has(key)) {
              seen3.add(key);
              merged3.push(item);
            }
          });
        }
      });
      setState((prevState) => ({ ...prevState, category3Options: createOptions(merged3, 'name', '_id') }));
    }

    if (
      Array.isArray(selected_Category1) &&
      selected_Category1.length &&
      Array.isArray(selected_Category2) &&
      selected_Category2.length &&
      Array.isArray(selected_Category3) &&
      selected_Category3.length
    ) {
      const c1s = selected_Category1;
      const c2s = selected_Category2;
      const c3s = selected_Category3;
      const calls4 = [];
      c1s.forEach((c1) => {
        c2s.forEach((c2) => {
          c3s.forEach((c3) => {
            calls4.push(
              dispatch(
                commonAction.fetchCategoryAction({
                  accessedFrom: 'masters',
                  categoryType: 4,
                  loggedInUserId: userId,
                  categoryOneId: c1,
                  categoryTwoId: c2,
                  categoryThreeId: c3,
                }),
              ),
            );
          });
        });
      });
      const results4 = await Promise.all(calls4);
      const merged4 = [];
      const seen4 = new Set();
      results4.forEach((res) => {
        const arr = Array.isArray(res?.data) ? res.data : res;
        if (Array.isArray(arr)) {
          arr.forEach((item) => {
            const key = String(item?._id ?? item?.value ?? '');
            if (key && !seen4.has(key)) {
              seen4.add(key);
              merged4.push(item);
            }
          });
        }
      });
      setState((prevState) => ({ ...prevState, category4Options: createOptions(merged4, 'name', '_id') }));
    }
  };

  const toIds = (val) => {
    if (Array.isArray(val))
      return val
        .map((v) => (typeof v === 'object' ? v?._id ?? v?.value : v))
        .filter((v) => v !== undefined && v !== null)
        .map((v) => String(v));
    if (typeof val === 'object') return [val?._id ?? val?.value].filter((v) => v !== undefined && v !== null).map((v) => String(v));
    return [val].filter((v) => v !== undefined && v !== null).map((v) => String(v));
  };
  const getIds = (arr) =>
    Array.isArray(arr)
      ? arr
          .map((v) => (typeof v === 'object' ? v?._id ?? v?.value : v))
          .filter((v) => v !== undefined && v !== null)
          .map((v) => String(v))
      : [];
  const selectedPTIds = getIds(selectedProductType);
  const selectedCat1Ids = getIds(selected_Category1);
  const selectedCat2Ids = getIds(selected_Category2);
  const selectedCat3Ids = getIds(selected_Category3);

  const selCat1Key = useMemo(() => (selectedCat1Ids.length ? selectedCat1Ids.slice().sort().join(',') : ''), [selectedCat1Ids]);
  const selCat2Key = useMemo(() => (selectedCat2Ids.length ? selectedCat2Ids.slice().sort().join(',') : ''), [selectedCat2Ids]);
  const selCat3Key = useMemo(() => (selectedCat3Ids.length ? selectedCat3Ids.slice().sort().join(',') : ''), [selectedCat3Ids]);

  const optionIds = (arr) =>
    Array.isArray(arr)
      ? arr
          .map((o) => String(o?._id ?? o?.value))
          .filter(Boolean)
          .sort()
      : [];
  const areOptionIdsEqual = (a, b) => {
    const aa = optionIds(a);
    const bb = optionIds(b);
    if (aa.length !== bb.length) return false;
    for (let i = 0; i < aa.length; i += 1) if (aa[i] !== bb[i]) return false;
    return true;
  };

  const category1Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category1Options;
    if (!selectedPTIds?.length) return category1Options;
    const base = category1Options || [];
    const filtered = base.filter((opt) => {
      const used = Array.isArray(opt?.isUsedProductType) ? opt.isUsedProductType : [];
      const usedIds = toIds(used);
      const pts = Array.isArray(opt?.productType) ? opt.productType : [];
      const ptIds = toIds(pts);
      return usedIds?.some((id) => selectedPTIds.includes(id)) || ptIds?.some((id) => selectedPTIds.includes(id));
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category1Options, selectedPTIds]);

  const category2Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category2Options;
    if (!selectedCat1Ids?.length) return category2Options;
    const base = category2Options || [];
    const hasMeta = base.some((opt) => Array.isArray(opt?.isUsedCat1) && opt.isUsedCat1.length);
    if (!hasMeta) return base;
    const filtered = base.filter((opt) => {
      const usedCat1 = Array.isArray(opt?.isUsedCat1) ? opt.isUsedCat1 : [];
      const usedIds = toIds(usedCat1);
      return usedIds?.some((id) => selectedCat1Ids.includes(id));
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category2Options, selectedCat1Ids]);

  const category3Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category3Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length) return category3Options;
    const base = category3Options || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId);
    if (!hasMeta) return base;
    const filtered = base.filter((opt) => {
      const c1Ids = toIds(opt?.categoryOneId);
      const c2Ids = toIds(opt?.categoryTwoId);
      const c1Match = c1Ids?.some((id) => selectedCat1Ids.includes(id));
      const c2Match = c2Ids?.some((id) => selectedCat2Ids.includes(id));
      return c1Match && c2Match;
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category3Options, selectedCat1Ids, selectedCat2Ids]);

  const category4Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category4Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length || !selectedCat3Ids?.length) return category4Options;
    const base = category4Options || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId && opt?.categoryThreeId);
    if (!hasMeta) return base;
    const filtered = base.filter((opt) => {
      const c1Ids = toIds(opt?.categoryOneId);
      const c2Ids = toIds(opt?.categoryTwoId);
      const c3Ids = toIds(opt?.categoryThreeId);
      const c1Match = c1Ids?.some((id) => selectedCat1Ids.includes(id));
      const c2Match = c2Ids?.some((id) => selectedCat2Ids.includes(id));
      const c3Match = c3Ids?.some((id) => selectedCat3Ids.includes(id));
      return c1Match && c2Match && c3Match;
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category4Options, selectedCat1Ids, selectedCat2Ids, selectedCat3Ids]);

  useEffect(() => {
    const brandId = selected_Brand;
    if (brandId) {
      dispatch(
        systemMasterAction.getMultiSubBrandDetails({
          brandId: Array.isArray(brandId) ? brandId : [brandId],
        }),
      );
    }
  }, [dispatch, selected_Brand]);

  useEffect(() => {
    if (Array.isArray(multiSubBrandList) && multiSubBrandList.length > 0) {
      const opts = createOptions(multiSubBrandList, 'name', '_id');
      setState((prev) => ({ ...prev, subBrandOptions: opts }));
    } else {
      setState((prev) => ({ ...prev, subBrandOptions: [] }));
    }
  }, [multiSubBrandList]);

  useEffect(() => {
    const callFn = async () => {
      const BrandRes = isSupplierLoggedIn
        ? await dispatch(getSupplierBrands(userId))
        : await dispatch(
            commonAction.fetchAllBrandAction({
              loggedInUserId: userId,
            }),
          );
      await fetchCategories();
      const sesonres = await dispatch(commonAction.getSeasonsAction({ loggedInUserId: userId }));

      if (!isSupplierLoggedIn) {
        const user_list = await dispatch(
          commonAction.getCompanyUsers({
            CompanyUsers: true,
          }),
        );
        if (checkArray(user_list)) {
          let user_options = createOptions(user_list, 'firstName', '_id');
          if (user_options && user_options.length > 0) {
            user_options = user_options.map((userdata) => {
              userdata.label = `${userdata?.firstName} ${userdata?.lastName}`;
              return userdata;
            });
          }
          setState((prevState) => ({
            ...prevState,
            usersOptions: user_options,
          }));
        }
      }

      if (checkArray(sesonres)) {
        const season_Options = createOptions(sesonres, 'name', '_id');
        setState((prevState) => ({
          ...prevState,
          seasonOptions: season_Options,
        }));
      }
      if (checkArray(BrandRes)) {
        const brand_Options = createOptions(BrandRes, 'name', '_id');
        setState((prevState) => ({
          ...prevState,
          brandOptions: brand_Options,
        }));
      }

      if (!isSupplierLoggedIn) {
        let supp_list = [];

        if (isCompanyUserLoggedIn) {
          supp_list = await dispatch(commonAction.getUserSuppliers());

          supp_list = supp_list?.suppliers;
          supp_list = supp_list?.length
            ? supp_list?.map((ele) => {
                if (ele.companies.length) {
                  ele.name = ele.companies[0].supplierName;
                }
                return ele;
              })
            : [];
        } else {
          supp_list = await dispatch(
            commonAction.getSuppliers({
              loggedInUserId: userId,
            }),
          );
        }
        if (checkArray(supp_list)) {
          const supplier_Options = createOptions(supp_list, 'name', '_id');
          setState((prevState) => ({
            ...prevState,
            supplierOptions: supplier_Options,
          }));
        }
      }

      const statusRes = await dispatch(
        getProductStatusDetails({
          loggedInUserId: userId,
        }),
      );

      if (statusRes?.code === 200) {
        const stats = await createOptions(statusRes?.data, 'name', '_id');
        setState((prev) => ({ ...prev, statusOptions: stats }));
      }
    };
    if (!isEmpty(userId)) callFn();
  }, [dispatch, userId]); // eslint-disable-line

  useEffect(() => {
    const callFn = async () => {
      if (isFetchingCatsRef.current) return;
      isFetchingCatsRef.current = true;
      try {
        const c1s = selectedCat1Ids;
        const c2s = selectedCat2Ids;
        const c3s = selectedCat3Ids;

        if (c1s.length && c2s.length && c3s.length && !isEmpty(userId)) {
          // Fetch Category 4 for every C1×C2×C3 combination and merge
          const calls = [];
          c1s.forEach((c1) => {
            c2s.forEach((c2) => {
              c3s.forEach((c3) => {
                calls.push(
                  dispatch(
                    commonAction.fetchCategoryAction({
                      accessedFrom: 'masters',
                      categoryType: 4,
                      loggedInUserId: userId,
                      categoryOneId: c1,
                      categoryTwoId: c2,
                      categoryThreeId: c3,
                    }),
                  ),
                );
              });
            });
          });
          const results = await Promise.all(calls);
          const merged = [];
          const seen = new Set();
          results.forEach((res) => {
            const arr = Array.isArray(res?.data) ? res.data : res;
            if (Array.isArray(arr)) {
              arr.forEach((item) => {
                const key = String(item?._id ?? item?.value ?? '');
                if (key && !seen.has(key)) {
                  seen.add(key);
                  merged.push(item);
                }
              });
            }
          });
          const next = createOptions(merged, 'name', '_id');
          if (!areOptionIdsEqual(category4Options, next)) {
            setState((prevState) => ({ ...prevState, [`category${4}Options`]: next }));
          }

          // Also hydrate Category 3 options if empty
          if (!Array.isArray(category3Options) || category3Options.length === 0) {
            const cat3Calls = [];
            c1s.forEach((c1) => {
              c2s.forEach((c2) => {
                cat3Calls.push(
                  dispatch(
                    commonAction.fetchCategoryAction({
                      accessedFrom: 'masters',
                      categoryType: 3,
                      loggedInUserId: userId,
                      categoryOneId: c1,
                      categoryTwoId: c2,
                    }),
                  ),
                );
              });
            });
            const res3 = await Promise.all(cat3Calls);
            const merged3 = [];
            const seen3 = new Set();
            res3.forEach((r) => {
              const arr = Array.isArray(r?.data) ? r.data : r;
              if (Array.isArray(arr)) {
                arr.forEach((item) => {
                  const key = String(item?._id ?? item?.value ?? '');
                  if (key && !seen3.has(key)) {
                    seen3.add(key);
                    merged3.push(item);
                  }
                });
              }
            });
            const next3 = createOptions(merged3, 'name', '_id');
            if (!areOptionIdsEqual(category3Options, next3)) {
              setState((prevState) => ({ ...prevState, [`category${3}Options`]: next3 }));
            }
          }
        } else if (c1s.length && c2s.length && !isEmpty(userId)) {
          // Fetch Category 3 for every C1×C2 combination and merge
          const calls = [];
          c1s.forEach((c1) => {
            c2s.forEach((c2) => {
              calls.push(
                dispatch(
                  commonAction.fetchCategoryAction({
                    accessedFrom: 'masters',
                    categoryType: 3,
                    loggedInUserId: userId,
                    categoryOneId: c1,
                    categoryTwoId: c2,
                  }),
                ),
              );
            });
          });
          const results = await Promise.all(calls);
          const merged = [];
          const seen = new Set();
          results.forEach((res) => {
            const arr = Array.isArray(res?.data) ? res.data : res;
            if (Array.isArray(arr)) {
              arr.forEach((item) => {
                const key = String(item?._id ?? item?.value ?? '');
                if (key && !seen.has(key)) {
                  seen.add(key);
                  merged.push(item);
                }
              });
            }
          });
          const next = createOptions(merged, 'name', '_id');
          if (!areOptionIdsEqual(category3Options, next)) {
            setState((prevState) => ({ ...prevState, [`category${3}Options`]: next }));
          }
        }
      } finally {
        isFetchingCatsRef.current = false;
      }
    };
    callFn();
  }, [selCat1Key, selCat2Key, selCat3Key, userId, catBasedOnPTData]);

  return (
    <button
      className={isApply ? 'filter-btn filter-button_delete active ' : 'filter-btn filter-button_delete '}
      type="button"
      title={t('PROGRESS_INDICATOR.PROGRESS_FILTER')}
      aria-label={t('PROGRESS_INDICATOR.PROGRESS_FILTER')}
    >
      <OverlayTrigger
        placement={t('FILE_TODO_LIST.BUTTON')}
        trigger="click"
        rootClose
        overlay={
          <Popover className="custom-popover horizontal-view">
            <Popover.Content>
              <div className="product-costing-filter">
                <div className="filter-text">
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="brand"
                        options={brandOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={brandOptions.filter((val) => !isEmpty(selected_Brand) && selected_Brand.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_BRAND'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_BRAND')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Brand',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  {subBrandAccess && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="subBrand"
                          options={subBrandOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={subBrandOptions.filter((val) => !isEmpty(selected_SubBrand) && selected_SubBrand.includes(val._id))}
                          overrideStrings={{
                            selectSomeItems: tReports('TODO_COMMON.SELECT_SUBBRAND'),
                          }}
                          labelledBy={tReports('TODO_COMMON.SELECT_SUBBRAND')}
                          onChange={(selected) =>
                            handleSelectChange(
                              selected.map((val) => val._id),
                              'selected_SubBrand',
                            )
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  {catBasedOnPTData && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="productType"
                          options={productTypeOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={productTypeOptions.filter((opt) => Array.isArray(selectedProductType) && selectedProductType.includes(opt.value))}
                          overrideStrings={{
                            selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1'),
                          }}
                          labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1')}
                          onChange={(selected) =>
                            handleSelectChange(Array.isArray(selected) ? selected.map((opt) => opt?.value).filter(Boolean) : [], 'selectedProductType')
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category1"
                        options={catBasedOnPTData ? category1Filtered : category1Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category1Filtered : category1Options).filter((val) => {
                          const sel = Array.isArray(selected_Category1) ? selected_Category1.map((v) => String(v)) : [];
                          const id = val?._id ?? val?.value;
                          return sel.length && sel.includes(String(id));
                        })}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val?._id ?? val?.value),
                            'selected_Category1',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category2"
                        options={catBasedOnPTData ? category2Filtered : category2Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category2Filtered : category2Options).filter((val) => {
                          const sel = Array.isArray(selected_Category2) ? selected_Category2.map((v) => String(v)) : [];
                          const id = val?._id ?? val?.value;
                          return sel.length && sel.includes(String(id));
                        })}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_2'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_2')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val?._id ?? val?.value),
                            'selected_Category2',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category3"
                        options={catBasedOnPTData ? category3Filtered : category3Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category3Filtered : category3Options).filter((val) => {
                          const sel = Array.isArray(selected_Category3) ? selected_Category3.map((v) => String(v)) : [];
                          const id = val?._id ?? val?.value;
                          return sel.length && sel.includes(String(id));
                        })}
                        overrideStrings={{
                          selectSomeItems: tReports('TODO_COMMON.SELECT_CATEGORY_3'),
                        }}
                        labelledBy={tReports('TODO_COMMON.SELECT_CATEGORY_3')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val?._id ?? val?.value),
                            'selected_Category3',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category4"
                        options={catBasedOnPTData ? category4Filtered : category4Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category4Filtered : category4Options).filter((val) => {
                          const sel = Array.isArray(selected_Category4) ? selected_Category4.map((v) => String(v)) : [];
                          const id = val?._id ?? val?.value;
                          return sel.length && sel.includes(String(id));
                        })}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_4'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_4')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val?._id ?? val?.value),
                            'selected_Category4',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="season"
                        options={seasonOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={seasonOptions.filter((val) => !isEmpty(selected_Season) && selected_Season.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SEASON'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_SEASON')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Season',
                          )
                        }
                      />
                    </div>
                  </FormGroup>

                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="year"
                        ItemRenderer={CurrentYearItemRenderer}
                        options={yearOptions || []}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={yearOptions?.filter((val) => selected_Year?.includes(val?._id)) || []}
                        overrideStrings={{
                          selectSomeItems: t('RANGE_PLANS.PRODUCT_LIST.FILTER_PRODUCT_LIST.SELECT_YEAR'),
                        }}
                        labelledBy={t('RANGE_PLANS.PRODUCT_LIST.FILTER_PRODUCT_LIST.SELECT_YEAR')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Year',
                          )
                        }
                      />
                    </div>
                  </FormGroup>

                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="month"
                        options={monthYearOptions}
                        disabled={!Array.isArray(selected_Year) || selected_Year.length === 0}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={monthYearOptions.filter((val) => selected_Month?.includes(val.value))}
                        overrideStrings={{
                          selectSomeItems: t('RANGE_PLANS.PRODUCT_LIST.FILTER_PRODUCT_LIST.SELECT_MONTH'),
                        }}
                        labelledBy={t('RANGE_PLANS.PRODUCT_LIST.FILTER_PRODUCT_LIST.SELECT_MONTH')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val.value),
                            'selected_Month',
                          )
                        }
                      />
                    </div>
                  </FormGroup>

                  {!isSupplierLoggedIn && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="supplier"
                          options={supplierOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={supplierOptions.filter((val) => !isEmpty(selected_Supplier) && selected_Supplier.includes(val._id))}
                          overrideStrings={{
                            selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SUPLIERS'),
                          }}
                          labelledBy={t('COSTING_TO_DO_LIST.SELECT_SUPLIERS')}
                          onChange={(selected) =>
                            handleSelectChange(
                              selected.map((val) => val._id),
                              'selected_Supplier',
                            )
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="supplier"
                        options={usersOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={usersOptions.filter((val) => !isEmpty(selected_CreatedBy) && selected_CreatedBy.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: tReports('TODO_COMMON.SELECTED_CREATE'),
                        }}
                        labelledBy={tReports('TODO_COMMON.SELECTED_CREATE')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_CreatedBy',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="supplier"
                        options={usersOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={usersOptions.filter((val) => !isEmpty(selected_ModifiedBy) && selected_ModifiedBy.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('PROGRESS_INDICATOR.SELECT_MODIFIE'),
                        }}
                        labelledBy={t('PROGRESS_INDICATOR.SELECT_MODIFIE')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_ModifiedBy',
                          )
                        }
                      />
                    </div>
                  </FormGroup>

                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="status"
                        options={statusOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={statusOptions.filter((val) => !isEmpty(selected_Status) && selected_Status.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('PROGRESS_INDICATOR.SELECT_STATUS'),
                        }}
                        labelledBy={t('PROGRESS_INDICATOR.SELECT_STATUS')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Status',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  {licensorAccess && (
                    <>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            options={licensorOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={licensorOptions.filter((val) => checkArray(selectedLicensor) && selectedLicensor.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LICENSOR'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LICENSOR')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id),
                                'selectedLicensor',
                              )
                            }
                          />
                        </div>
                      </FormGroup>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            options={licenseOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={licenseOptions.filter((val) => checkArray(selectedLicense) && selectedLicense.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LICENSE'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LICENSE')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id),
                                'selectedLicense',
                              )
                            }
                          />
                        </div>
                      </FormGroup>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            options={likenessOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={likenessOptions.filter((val) => checkArray(selectedLikeness) && selectedLikeness.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LIKENESS'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LIKENESS')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id),
                                'selectedLikeness',
                              )
                            }
                          />
                        </div>
                      </FormGroup>
                    </>
                  )}
                </div>

                <div className="d-flex justify-content-end">
                  <button type="button" aria-label="Clear" className="btn p-0 d-inline-flex" onClick={clearAll}>
                    <span className="icon-Clear-New f-24" />
                  </button>
                  <button type="button" aria-label="Apply" className="btn p-0 d-inline-flex ml-16" onClick={applyFilter}>
                    <span className="icon-Tick-1 f-28" />
                  </button>
                </div>
              </div>
            </Popover.Content>
          </Popover>
        }
      >
        <i className="icon-Filter f-26" />
      </OverlayTrigger>
    </button>
  );
}

export default IndicatorFilter;
